// getTimeRangeBarList.js
'use strict';

import { EMPTY_ARRAY } from '../resource/defaults.js';

const clampPercentage = value => Math.max(0, Math.min(100, value || 0));

/**
 * Lay media time ranges onto a bar as percentages of that bar's width.
 * Returns an empty list when the timeline has no length, since dividing by it
 * would size every bar to `Infinity%`.
 * @param {object} param
 * @param {Array<{start: number, end: number}>} [param.ranges] - time ranges.
 * @param {number} [param.min] - timeline start in seconds.
 * @param {number} [param.max] - timeline end in seconds.
 * @returns {Array<{start: number, startPercentage: number, widthPercentage: number}>}
 * one entry per range, keeping `start` so callers can key their elements on it.
 * @example
 * getTimeRangeBarList({ ranges: [{ start: 30, end: 40 }], max: 100 });
 * // => [{ start: 30, startPercentage: 30, widthPercentage: 10 }]
 */
const getTimeRangeBarList = ({ ranges, min = 0, max } = {}) => {
  const timelineLength = max - min;
  if (!ranges?.length || !(timelineLength > 0)) return EMPTY_ARRAY;

  // Clamp both ends before measuring the width. Clamping the width on its own
  // leaves it unaware of where the bar starts, so a range ending past the
  // timeline still gets its full width added to an already offset start and the
  // bar runs past the track.
  return ranges.map(({ start, end }) => {
    const startPercentage = clampPercentage(
      ((start - min) / timelineLength) * 100
    );
    const endPercentage = clampPercentage(((end - min) / timelineLength) * 100);

    return {
      start,
      startPercentage,
      widthPercentage: Math.max(0, endPercentage - startPercentage),
    };
  });
};

export default getTimeRangeBarList;
